import { useCallback } from 'react';
import { Task, FullProject, LogEntry, TaskHistoryEntry } from '../../../types';
import {
  isInboxProject,
  isCommProject,
  areProjectIdsMatching,
  isMatchingTaskId,
} from '../../../utils/idNormalizer';
import { buildTaskHistoryEntries } from './taskHistoryUtils';
import { ProjectSyncTracker } from '../sync/projectSyncStateTracker';

export interface UseTaskDuplicateAndMoveProps {
  syncTracker: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  saveSnapshot: () => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  updateTask: (id: number | string, updates: Partial<Task>, projectId?: string) => void;
  updateCurrentProjectTasks: (currentProj: FullProject, updatedProjList: FullProject[]) => void;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
}

export function useTaskDuplicateAndMove({
  syncTracker,
  setAllProjects,
  currentProjectId,
  tasks,
  setTasks,
  saveSnapshot,
  addLogEntry,
  markProjectDirty,
  onMarkTaskDirty,
  updateTask,
  updateCurrentProjectTasks,
  user,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
}: UseTaskDuplicateAndMoveProps) {
  const handleSaveTask = useCallback((taskToSave: Task) => {
    const { 
      id, 
      projectId: targetProjectId, 
      // Strip UI metadata
      projectName: _pn, projectColor: _pc, projectBadge: _pb, globalStatus: _gs,
      ...updates 
    } = taskToSave as Task & { projectBadge?: unknown; globalStatus?: unknown };

    const allProjList = syncTracker.getAllProjects();
    
    if (id === undefined || id === null) {
      console.warn("[ProjectManager] handleSaveTask skipped: taskToSave has no valid ID", taskToSave);
      return;
    }
    const safeIdStr = String(id);
    let originalTask: Task | undefined;
    const originalProject = allProjList.find(p => {
      const found = (p.tasks || []).find(t => t && t.id !== undefined && t.id !== null && (isMatchingTaskId(id, t.id, p.id) || String(t.id) === safeIdStr));
      if (found) originalTask = found;
      return !!found;
    });

    const rawTargetProjectId = targetProjectId === 'inbox_inforum' ? INBOX_PROJECT_ID : (targetProjectId === 'comm_internal' ? COMM_PROJECT_ID : targetProjectId);
    const finalProjectId = rawTargetProjectId || (originalProject ? originalProject.id : currentProjectId);
    const normalizedFinalProjectId = normalizeProjectId(finalProjectId);

    if (!originalProject || !originalTask) {
      console.warn("[ProjectManager] handleSaveTask: task not found in any project snapshot, recovering into project:", normalizedFinalProjectId, "ID:", id);
      const newTask: Task = {
        ...(taskToSave as Task),
        projectId: normalizedFinalProjectId,
        updatedAt: Date.now()
      };
      
      const updatedProjects = syncTracker.getAllProjects().map(p => {
        if (areProjectIdsMatching(p.id, normalizedFinalProjectId)) {
          return { ...p, tasks: [...(p.tasks || []).filter(t => !isMatchingTaskId(id, t.id, p.id) && String(t.id) !== safeIdStr), newTask], lastModified: Date.now() };
        }
        return p;
      });
      syncTracker.commitProjects(
        updatedProjects,
        setAllProjects,
        normalizedFinalProjectId ? [normalizedFinalProjectId] : undefined
      );
      syncTracker.syncTask(newTask, normalizedFinalProjectId, true);

      if (areProjectIdsMatching(normalizedFinalProjectId, currentProjectId)) {
        setTasks(prev => [...prev.filter(t => !isMatchingTaskId(id, t.id, t.projectId) && String(t.id) !== safeIdStr), newTask]);
      }
      if (normalizedFinalProjectId) {
        markProjectDirty(normalizedFinalProjectId);
      }
      onMarkTaskDirty(newTask.id, normalizedFinalProjectId);
      return;
    }

    const sourceProjectId = originalProject.id;

    if (!areProjectIdsMatching(sourceProjectId, normalizedFinalProjectId)) {
      // PROJECT MOVE
      saveSnapshot();
      addLogEntry('update', `Aufgabe "${taskToSave.name}" von einem Projekt in ein anderes verschoben`);
      
      const targetProjObj = allProjList.find(p => areProjectIdsMatching(p.id, normalizedFinalProjectId));
      const actualFinalProjectId = targetProjObj?.id || normalizedFinalProjectId;
      const targetProjName = targetProjObj?.name || normalizedFinalProjectId;
      
      const userStr = user?.email || 'System';
      const moveEntry: TaskHistoryEntry = {
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        user: userStr,
        field: 'projectId',
        oldValue: sourceProjectId,
        newValue: actualFinalProjectId,
        message: `Aufgabe in das Projekt "${targetProjName}" verschoben`
      };

      const historyEntries = buildTaskHistoryEntries(
        originalTask,
        updates,
        userStr,
        ['name', 'description', 'start', 'end', 'status', 'assignee', 'priority', 'completed', 'type'],
        [moveEntry]
      );

      const existingHistory = originalTask.history || [];
      const finalHistory = [...historyEntries, ...existingHistory].slice(0, 100);
      const nextProjectIds = actualFinalProjectId && !isCommProject(actualFinalProjectId) && !isInboxProject(actualFinalProjectId)
        ? Array.from(new Set([
            ...((updates.projectIds || originalTask.projectIds || []).filter((pid: string) => !areProjectIdsMatching(pid, sourceProjectId))),
            actualFinalProjectId
          ]))
        : (updates.projectIds || originalTask.projectIds);

      let movedTaskObj: Task | undefined;

      const updatedProjects = allProjList.map(p => {
        if (areProjectIdsMatching(p.id, sourceProjectId)) {
          const modP = { ...p, tasks: p.tasks.filter(t => !isMatchingTaskId(id, t.id, p.id)), lastModified: Date.now() };
          markProjectDirty(modP.id);
          return modP;
        }
        if (areProjectIdsMatching(p.id, actualFinalProjectId)) {
          const newTask: Task = { 
            ...originalTask!, 
            ...updates, 
            projectId: p.id,
            ...(nextProjectIds ? { projectIds: nextProjectIds } : {}),
            history: finalHistory,
            updatedAt: Date.now(),
            sortOrder: updates.sortOrder ?? (p.tasks.length > 0 ? Math.max(...p.tasks.map(t => t.sortOrder || 0)) + 1000 : 1000)
          };
          movedTaskObj = newTask;
          const modP = { ...p, tasks: [...p.tasks.filter(t => !isMatchingTaskId(id, t.id, p.id)), newTask], lastModified: Date.now() };
          markProjectDirty(modP.id);
          onMarkTaskDirty(newTask.id, modP.id);
          return modP;
        }
        return p;
      });

      if (movedTaskObj) {
        syncTracker.syncTask(movedTaskObj, actualFinalProjectId);
      }
      syncTracker.commitProjects(
        updatedProjects,
        setAllProjects,
        [sourceProjectId, actualFinalProjectId || ''].filter(Boolean)
      );
      
      const currentInNew = updatedProjects.find(p => areProjectIdsMatching(p.id, currentProjectId));
      if (currentInNew) {
        updateCurrentProjectTasks(currentInNew, updatedProjects);
      }
    } else {
      // SAME PROJECT UPDATE - Delegate to updateTask for consistency (and auto-scheduling)
      updateTask(id, updates, sourceProjectId);
    }
  }, [currentProjectId, saveSnapshot, addLogEntry, updateTask, user?.email, syncTracker, INBOX_PROJECT_ID, COMM_PROJECT_ID, normalizeProjectId, markProjectDirty, onMarkTaskDirty, setAllProjects, setTasks, updateCurrentProjectTasks]);

  const duplicateTask = useCallback((taskId: string | number, targetProjectId?: string, overrides?: Partial<Task>) => {
    saveSnapshot();

    // 1. Find which project this task belongs to
    let originalTask: Task | undefined;
    let sourceProject: FullProject | undefined;
    const safeTaskIdStr = String(taskId);
    const currentAllProjects = syncTracker.getAllProjects();

    for (const p of currentAllProjects) {
      const found = p.tasks.find(t => isMatchingTaskId(taskId, t.id, p.id) || String(t.id) === safeTaskIdStr);
      if (found) {
        originalTask = found;
        sourceProject = p;
        break;
      }
    }

    if (!originalTask || !sourceProject) {
      const foundInCurrent = tasks.find(t => isMatchingTaskId(taskId, t.id, currentProjectId) || String(t.id) === safeTaskIdStr);
      if (foundInCurrent) {
        originalTask = foundInCurrent;
        sourceProject = currentAllProjects.find(p => areProjectIdsMatching(p.id, currentProjectId)) || currentAllProjects[0];
      }
    }

    if (!originalTask || !sourceProject) {
      console.warn(`[ProjectManager] duplicateTask failed: Task ${taskId} not found.`);
      return;
    }

    const finalProjectId = targetProjectId || sourceProject.id;
    const normalizedFinalProjectId = normalizeProjectId(finalProjectId);
    const targetProject = currentAllProjects.find(p => p.id === normalizedFinalProjectId);
    if (!targetProject) return;

    const isSameProject = normalizedFinalProjectId === sourceProject.id;
    const newTaskId = Date.now().toString() + Math.random().toString(36).substring(2, 5);

    // Create duplicated main task
    const duplicatedTask: Task = {
      ...originalTask,
      ...(overrides || {}),
      id: newTaskId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      projectId: normalizedFinalProjectId,
      tenantId: targetProject.tenantId || tenantId,
      name: overrides?.name !== undefined ? overrides.name : originalTask.name,
      parentId: isSameProject ? (overrides?.parentId !== undefined ? overrides.parentId : originalTask.parentId) : undefined,
      dependencies: isSameProject ? [...(overrides?.dependencies || originalTask.dependencies || [])] : [],
      timerStartedAt: undefined,
      trackedTime: undefined,
      history: [
        {
          id: Math.random().toString(36).substr(2, 9),
          timestamp: Date.now(),
          user: user?.email || 'System',
          field: 'duplicate',
          message: isSameProject
            ? `Aufgabe dupliziert von "${originalTask.name}"`
            : `Aufgabe kopiert aus dem Projekt "${sourceProject.name}"`
        }
      ]
    };

    // Find and clone subtasks recursively (only when duplicating in same project)
    const originalSubtasks = sourceProject.tasks.filter(t => t.parentId !== undefined && t.parentId !== null && String(t.parentId) === safeTaskIdStr);
    const duplicatedSubtasks: Task[] = [];

    if (isSameProject && originalSubtasks.length > 0) {
      originalSubtasks.forEach(sub => {
        const newSubId = Date.now().toString() + Math.random().toString(36).substring(2, 5);
        duplicatedSubtasks.push({
          ...sub,
          id: newSubId,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          projectId: normalizedFinalProjectId,
          tenantId: targetProject.tenantId || tenantId,
          parentId: newTaskId,
          dependencies: [],
          timerStartedAt: undefined,
          trackedTime: undefined,
          history: [
            {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: Date.now(),
              user: user?.email || 'System',
              field: 'duplicate',
              message: `Unteraufgabe dupliziert als Teil der übergeordneten Aufgabe "${originalTask!.name}"`
            }
          ]
        });
      });
    }

    const nextSortOrder = targetProject.tasks.length > 0
      ? Math.max(...targetProject.tasks.map(t => t.sortOrder || 0)) + 1000
      : 1000;

    const tasksToAdd = [
      { ...duplicatedTask, sortOrder: nextSortOrder },
      ...duplicatedSubtasks.map((sub, idx) => ({ ...sub, sortOrder: nextSortOrder + (idx + 1) * 10 }))
    ];

    const updatedProjects = currentAllProjects.map(p => {
      if (p.id === normalizedFinalProjectId) {
        const modP = {
          ...p,
          tasks: [...p.tasks, ...tasksToAdd],
          lastModified: Date.now()
        };
        markProjectDirty(modP.id);
        tasksToAdd.forEach(t => onMarkTaskDirty(t.id, modP.id));
        return modP;
      }
      return p;
    });

    syncTracker.syncMultipleTasks(tasksToAdd, true);
    syncTracker.commitProjects(
      updatedProjects,
      setAllProjects,
      normalizedFinalProjectId ? [normalizedFinalProjectId] : []
    );

    const activeProj = updatedProjects.find(p => areProjectIdsMatching(p.id, currentProjectId));
    if (activeProj) {
      updateCurrentProjectTasks(activeProj, updatedProjects);
    }

    addLogEntry('create', `Aufgabe "${originalTask.name}" dupliziert`);
    return duplicatedTask;
  }, [currentProjectId, tenantId, saveSnapshot, addLogEntry, markProjectDirty, onMarkTaskDirty, setAllProjects, user?.email, syncTracker, tasks, normalizeProjectId, updateCurrentProjectTasks]);

  return {
    handleSaveTask,
    duplicateTask,
  };
}
